import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";

import ThumbUpIcon from "@material-ui/icons/ThumbUpAltOutlined";
import ThumbDownIcon from "@material-ui/icons/ThumbDownAltOutlined";
import GavelIcon from "@material-ui/icons/Gavel";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import { AuthContext } from "../../context/Auth/AuthContext";

const STATUS_META = {
  pending: { label: "Aguardando", tone: "warning" },
  approved: { label: "Aprovado", tone: "success" },
  rejected: { label: "Recusado", tone: "danger" },
  canceled: { label: "Cancelado", tone: "neutral" },
};

const ENTITY_LABEL = {
  proposal: "Proposta",
  order: "Pedido",
  deal: "Negócio",
};

const useStyles = makeStyles((theme) => ({
  reason: { display: "block", color: theme.palette.text.secondary },
  when: {
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },
  muted: { color: theme.palette.text.hint },
  selfNote: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
}));

const Approvals = () => {
  const classes = useStyles();
  const { user } = React.useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [decision, setDecision] = useState({
    open: false,
    approval: null,
    approve: true,
    note: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals", {
        params: status ? { status } : {},
      });
      setRows(data?.approvals || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  useSocketSubscription("approval", load);

  const submitDecision = async () => {
    try {
      await api.post(`/approvals/${decision.approval.id}/decide`, {
        approve: decision.approve,
        note: decision.note || null,
      });
      toast.success(decision.approve ? "Aprovado." : "Recusado.");
      setDecision({ open: false, approval: null, approve: true, note: "" });
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const columns = [
    {
      key: "entity",
      label: "Documento",
      render: (a) => (
        <div>
          <Typography variant="subtitle2">{a.entityLabel}</Typography>
          <Typography variant="caption" className={classes.reason}>
            {ENTITY_LABEL[a.entityType] || a.entityType}
          </Typography>
        </div>
      ),
    },
    {
      key: "reason",
      label: "Motivo",
      render: (a) => a.reason,
    },
    {
      key: "requestedBy",
      label: "Solicitado por",
      render: (a) => a.requestedBy?.name || <span className={classes.muted}>—</span>,
    },
    {
      key: "createdAt",
      label: "Quando",
      render: (a) => (
        <span className={classes.when}>
          {new Date(a.createdAt).toLocaleString("pt-BR")}
        </span>
      ),
    },
    {
      key: "status",
      label: "Situação",
      render: (a) => {
        const meta = STATUS_META[a.status] || { label: a.status, tone: "neutral" };
        return <StatusChip dot tone={meta.tone} label={meta.label} />;
      },
    },
    {
      key: "decidedBy",
      label: "Decidido por",
      render: (a) =>
        a.decidedBy?.name || <span className={classes.muted}>—</span>,
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (a) => {
        if (a.status !== "pending") return null;

        // Quem pediu não decide — a regra vive no backend, mas mostrar o botão
        // aqui só para ele levar 403 no clique seria enganar o usuário.
        const isRequester =
          a.requestedById && Number(a.requestedById) === Number(user?.id);
        if (isRequester) {
          return (
            <Tooltip title="Você solicitou esta aprovação; quem pede não decide.">
              <span className={classes.muted}>aguardando outra pessoa</span>
            </Tooltip>
          );
        }

        return (
          <>
            <Tooltip title="Aprovar">
              <IconButton
                size="small"
                onClick={() =>
                  setDecision({ open: true, approval: a, approve: true, note: "" })
                }
              >
                <ThumbUpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Recusar">
              <IconButton
                size="small"
                onClick={() =>
                  setDecision({ open: true, approval: a, approve: false, note: "" })
                }
              >
                <ThumbDownIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  return (
    <MainContainer>
      <PageHeader
        title="Aprovações"
        count={rows.length}
        subtitle="Documentos que passaram do limite definido nas regras e esperam decisão para seguir."
        filters={
          <TextField
            select
            label="Situação"
            style={{ minWidth: 180 }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(STATUS_META).map(([value, meta]) => (
              <MenuItem key={value} value={value}>
                {meta.label}
              </MenuItem>
            ))}
          </TextField>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon={<GavelIcon />}
        emptyTitle={
          status === "pending"
            ? "Nada esperando decisão"
            : "Nenhuma aprovação neste filtro"
        }
        emptyDescription="As pendências aparecem quando uma proposta ou pedido passa do limite de desconto configurado nas regras de aprovação."
      />

      <Dialog
        open={decision.open}
        onClose={() =>
          setDecision({ open: false, approval: null, approve: true, note: "" })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {decision.approve ? "Aprovar" : "Recusar"} — {decision.approval?.entityLabel}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{decision.approval?.reason}</DialogContentText>
          <TextField
            label={decision.approve ? "Observação (opcional)" : "Motivo da recusa"}
            fullWidth
            multiline
            rows={3}
            margin="dense"
            value={decision.note}
            onChange={(e) =>
              setDecision((d) => ({ ...d, note: e.target.value }))
            }
          />
          {decision.approve && (
            <div className={classes.selfNote}>
              A aprovação vale para este valor. Se o desconto aumentar depois, uma
              nova autorização será exigida.
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDecision({ open: false, approval: null, approve: true, note: "" })
            }
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={decision.approve ? "primary" : "secondary"}
            onClick={submitDecision}
          >
            {decision.approve ? "Aprovar" : "Recusar"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Approvals;
